import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient as defaultClient, type ApiClient } from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { FormField } from '../components/FormField';
import { TextInput } from '../components/TextInput';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import { useAuth } from '../auth/context';
import { useOrg } from '../org/context';
import { toDisplayError } from '../api/errorMessages';
import { isApiError } from '../api/errors';
import styles from './CreateOrganization.module.css';

interface FormSummary {
  message: string;
  requestId?: string;
}

export interface CreateOrganizationProps {
  /** Injectable client (tests); defaults to the shared apiClient. */
  client?: ApiClient;
}

export function CreateOrganization({ client = defaultClient }: CreateOrganizationProps) {
  const { logout } = useAuth();
  const { addOrg } = useOrg();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [summary, setSummary] = useState<FormSummary | null>(null);
  const [busy, setBusy] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const summaryRef = useRef<HTMLDivElement>(null);

  // Focus the error summary after it commits (deterministic vs rAF).
  useEffect(() => {
    if (summary) summaryRef.current?.focus();
  }, [summary]);

  const focusSummary = () => {
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy) return;

    if (!name.trim()) {
      setNameError('Enter an organization name.');
      setSummary({ message: 'Please fix the errors below and try again.' });
      focusSummary();
      return;
    }

    setNameError(undefined);
    setSummary(null);
    setBusy(true);
    try {
      const created = await client.createOrg(name.trim());
      addOrg(created);
      navigate('/', { replace: true });
    } catch (err) {
      if (isApiError(err) && err.status === 409) {
        setNameError('An organization with this name already exists.');
        setSummary({ message: 'Please fix the errors below and try again.' });
      } else {
        const display = toDisplayError(err);
        setSummary({
          message: display.message,
          ...(display.requestId ? { requestId: display.requestId } : {}),
        });
      }
      focusSummary();
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await logout();
    } finally {
      setSigningOut(false);
      navigate('/sign-in', { replace: true });
    }
  };

  return (
    <AppShell
      brand={
        <span className={styles.banner}>
          <span>TestCraft Journeys</span>
          <Button variant="ghost" busy={signingOut} onClick={onSignOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </Button>
        </span>
      }
    >
      <Card>
        <form className={styles.form} onSubmit={onSubmit} noValidate>
          <h1 className={styles.heading}>Create your organization</h1>
          <p className={styles.subheading}>
            An organization groups your journeys, defects, and teammates.
          </p>

          <div className={styles.summary}>
            <InlineAlert
              ref={summaryRef}
              severity="error"
              {...(summary?.requestId ? { requestId: summary.requestId } : {})}
            >
              {summary?.message}
            </InlineAlert>
          </div>

          <FormField
            label="Organization name"
            id="org-name"
            {...(nameError ? { error: nameError } : {})}
          >
            {({ controlProps }) => (
              <TextInput
                {...controlProps}
                type="text"
                name="org-name"
                // Single-field primary form: focusing the sole input on load is
                // an expected, low-risk convenience (WCAG 3.2.1 unaffected).
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            )}
          </FormField>

          <div className={styles.actions}>
            <Button type="submit" busy={busy} fullWidth>
              {busy ? 'Creating…' : 'Create organization'}
            </Button>
          </div>
        </form>
      </Card>
    </AppShell>
  );
}
